import { create } from 'zustand';
import { ORG_SUSPENDED_CODE, registerOrgBlockHandler, type OrgBlock } from '@/lib/orgBlock';
import { useOrganizationStore } from '@/store/organizationStore';

/**
 * T1192 — globalno stanje »organizacija je zaklenjena«.
 *
 * Polni ga izključno odgovorni interceptor v `services/api.ts` prek
 * `reportOrgBlock`; nobena stran ga ne nastavlja sama. Zaslon, ki ga bere, mora
 * delovati, ko je večina API-ja zaprta, zato so vsi podatki zanj že tu — iz
 * telesa napake, brez dodatnega klica.
 */
interface OrgBlockState {
  block: OrgBlock | null;
  setBlock: (block: OrgBlock) => void;
  clear: () => void;
}

function sameBlock(a: OrgBlock | null, b: OrgBlock): boolean {
  if (a === null || a.code !== b.code) return false;
  // T1255: WS pot zaklep javi **brez** identitete organizacije — close dogodek
  // telesa nima, koda je vse. Tak zapis ne sme povoziti bogatejšega iz telesa
  // 403, sicer bi zaslon izgubil ime organizacije, ki ga med zaklepom ni od kod
  // znova dobiti (organizacijski endpointi so zaprti).
  if (b.organization === null) return true;
  return (a.organization?.id ?? null) === b.organization.id;
}

export const useOrgBlockStore = create<OrgBlockState>((set, get) => ({
  block: null,

  setBlock: (block) => {
    // Ob zaklepu naenkrat pade več vzporednih poizvedb (drevo, članstva,
    // moduli …). Vsaka javi isti zaklep; brez te primerjave bi vsaka zamenjala
    // objekt v stanju in sprožila nov izris zaslona.
    if (sameBlock(get().block, block)) return;
    set({ block });
  },

  clear: () => set({ block: null }),
}));

registerOrgBlockHandler((block) => {
  useOrgBlockStore.getState().setBlock(block);

  // T1195 — plačilna pot iz zaklepa. `organizations/` je ob suspenziji zaprt,
  // zato bi `currentOrganization` ostal `null` in `/billing` bi se izrisal kot
  // prazna stran: zaklep brez ključa. Telo 403 identiteto nosi prav zato.
  //
  // Samo pri **suspenziji**: pri zaprtju je `billing` zaprt tudi na backendu,
  // `App.tsx` prevzame tudi `/billing` in plačilne strani sploh ni — seedanje
  // bi tam ustvarilo nepopolno organizacijo brez uporabnika.
  if (block.code === ORG_SUSPENDED_CODE && block.organization) {
    useOrganizationStore.getState().seedFromBlockedOrganization(block.organization);
  }
});
